<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="solar-installation-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.solarInstallation.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="solar-installation-page-title"
        />
      </div>

      <div data-cy="installed-power-in-kwc-input">
        <MoleculeLabel
          :text="t('subscription.solarInstallation.form.installedPowerInkWc.label')"
          class="mb-1 mt-6"
          label-for="installedPowerInkWc"
        />
        <MoleculeInputNumber
          id="installedPowerInkWc"
          v-model="installedPowerInkWc"
          step="0.1"
          :addon-text="{
            value: 'kWc',
            isPrefix: false,
          }"
          placeholder="0"
          addon-type="text"
          name="installedPowerInkWc"
          :disabled="isFormDisabled"
          :input-state="
            getInputState({ errorMessage: isSubmitted ? installedPowerInkWcError : undefined })
          "
          :helper="
            getHelperMessage({
              errorMessage: installedPowerInkWcError,
              infoMessage: t('subscription.solarInstallation.form.installedPowerInkWc.helper.info'),
            })
          "
        />
      </div>

      <div v-if="!getHasRobinDevice" data-cy="installation-performer" class="mt-6">
        <MoleculeLabel
          label-for="installationPerformedBy"
          :text="t('subscription.solarInstallation.form.installationPerformedBy.label')"
          class="font-medium mb-1"
        />
        <OrganismRadioGroupCard
          v-model="installationPerformedBy"
          :cards="installationPerformerChoices"
          name="installationPerformedBy"
          :error-message="installationPerformedByError"
          :has-error="isSubmitted && !!installationPerformedByError"
          data-cy="installation-performer-radio-group-cards"
          :disabled="isFormDisabled"
          orientation="horizontal"
        />
      </div>

      <OrganismAlert
        v-if="isSelfInstalled"
        variant="complex"
        :title="t('subscription.solarInstallation.selfInstalledInfoAlert.title')"
        :content-html="t('subscription.solarInstallation.selfInstalledInfoAlert.description')"
        class="mt-6"
        data-cy="self-installed-alert-info"
        state="info"
      />

      <template v-if="isInstallerInformationRequired">
        <AtomDivider class="my-8" />
        <AtomText
          :text="t('subscription.solarInstallation.form.yourInstaller.title')"
          font-style="font-semibold"
          font-size="text-lg"
          data-cy="your-installer-title"
        />

        <InstallerForm ref="installerFormRef" :disabled="isFormDisabled" />
      </template>
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useField } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { RgvRadioGroupCard } from '@/types/regenerativ.type';
import type { InstallationPerformer, SubscriptionInstallation } from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';

import InstallerForm from '@/components/subscription/InstallerForm.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EInstallationPerformer } from '@/constants/subscription.constants';

const { t } = useI18n();

const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { getInputState, getHelperMessage } = useInputState();

const { goToNextStep } = stepStore;

const {
  isSaveSubscriptionInProgress,
  isGetSubscriptionInProgress,
  isGetSubscriptionSuccessfully,
  getHasRobinDevice,
  getCurrentSubscriptionInstallation,
} = storeToRefs(subscriptionStore);

const {
  value: installedPowerInkWc,
  errorMessage: installedPowerInkWcError,
  validate: validateInstalledPowerInkWc,
} = useField<number>(
  'installedPowerInkWc',
  yup
    .number()
    .required(t('common.forms.required'))
    .max(100, t('subscription.solarInstallation.form.installedPowerInkWc.helper.info'))
);

const {
  value: installationPerformedBy,
  errorMessage: installationPerformedByError,
  validate: validateInstallationPerformedBy,
} = useField<string>('installationPerformedBy', yup.string().required(t('common.forms.required')));

const installerFormRef = ref<InstanceType<typeof InstallerForm> | null>(null);

const installationPerformerChoices = ref<RgvRadioGroupCard[]>(
  [EInstallationPerformer.PROFESSIONAL_INSTALLER, EInstallationPerformer.SELF_INSTALLED].map(
    choice => ({
      id: choice,
      title: t(`subscription.solarInstallation.form.installationPerformedBy.options.${choice}`),
      value: choice,
    })
  )
);

const isSubmitted = ref(false);

const isFormDisabled = computed(
  () => isGetSubscriptionInProgress.value || isSaveSubscriptionInProgress.value
);

const isInstallationPerformedByProfessionnel = computed(
  () => installationPerformedBy.value === EInstallationPerformer.PROFESSIONAL_INSTALLER
);

const isSelfInstalled = computed(
  () => installationPerformedBy.value === EInstallationPerformer.SELF_INSTALLED
);

const isInstallerInformationRequired = computed(
  () => isInstallationPerformedByProfessionnel.value || getHasRobinDevice.value
);

const setDefautValues = () => {
  if (!getCurrentSubscriptionInstallation.value) {
    return;
  }

  installedPowerInkWc.value = getCurrentSubscriptionInstallation.value?.installedPowerInkWc || 0;
  installationPerformedBy.value =
    getCurrentSubscriptionInstallation.value?.installationPerformedBy || '';
};

const getInstallationData = (): SubscriptionInstallation => ({
  installedPowerInkWc: Number(installedPowerInkWc.value),
  installationPerformedBy: getHasRobinDevice.value
    ? EInstallationPerformer.PROFESSIONAL_INSTALLER
    : (installationPerformedBy.value as InstallationPerformer),
  professionalInstaller: isInstallerInformationRequired.value
    ? installerFormRef.value!.getValues()
    : null,
});

const onSubmit = async () => {
  isSubmitted.value = true;

  toastSnackBar.clearToasts();

  const [installedPowerInkWcValidation, installationPerformedByValidation, installerValidation] =
    await Promise.all([
      validateInstalledPowerInkWc(),
      !getHasRobinDevice.value
        ? validateInstallationPerformedBy()
        : Promise.resolve({ valid: true }),
      isInstallerInformationRequired.value
        ? installerFormRef.value!.validate()
        : Promise.resolve({ valid: true }),
    ]);

  if (
    !installedPowerInkWcValidation.valid ||
    !installationPerformedByValidation.valid ||
    !installerValidation.valid
  ) {
    return;
  }

  try {
    const payload = getInstallationData();

    await subscriptionStore.updateSubscriptionWithInstallation(payload);

    goToNextStep();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'solar-installation-error',
      defaultMessage: t('subscription.equipments.api.errors.patchSubscriptionConsumption'),
    });

    toastSnackBar.addToast(toastConfig);
  }
};

watch(isGetSubscriptionSuccessfully, isSuccessfully => {
  if (isSuccessfully) {
    setDefautValues();
  }
});

onMounted(() => {
  setDefautValues();
});
</script>

<style scoped></style>
